import { useDefaultEmptyFieldError } from '@/hooks';
import {
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Row,
  Statistic,
} from 'antd';
import BankAccountInfiniteSelectField from '../../BankAccountInfiniteSelectField';
import DocumentTypesInfiniteSelectField from '../../DocumentTypesInfiniteSelectField';
import FloatingFormItem from '../../FloatingFormItem';
import InputDate from '../../InputDate';
import InputDecimal from '../../InputDecimal';
import { SettleInstallmentPartiallyFormValues } from './types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import formatterPrice from '@/utils/number/formatterPrice';

type Props = {
  form: FormInstance<SettleInstallmentPartiallyFormValues>;
};

const SettleInstallmentPartiallyForm: React.FC<Props> = ({ form }) => {
  const requiredRule = useDefaultEmptyFieldError();
  const installmentValue = Form.useWatch('installmentValue', form);
  const discountIncrease = Form.useWatch('discountIncrease', form);
  const rateValue = Form.useWatch('rateValue', form);

  const maxAmountToBeSettled =
    (installmentValue ?? 0) + (discountIncrease ?? 0);

  const netValue = maxAmountToBeSettled - (rateValue || 0);

  return (
    <Form
      form={form}
      onValuesChange={(_, fields) => {
        form.setFieldValue(
          'netValue',
          (fields.installmentValue || 0) + (fields.discountIncrease || 0),
        );
      }}
    >
      <Form.Item name="rateValue" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="netValue" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="installmentValue" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="discountIncrease" hidden>
        <InputNumber />
      </Form.Item>
      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="installmentReceivableId" hidden>
        <Input />
      </Form.Item>

      <Row gutter={[12, 12]}>
        <Col span={24}>
          <FloatingFormItem
            name="bankAccountId"
            label="Conta bancária"
            rules={[requiredRule]}
          >
            <BankAccountInfiniteSelectField queryFilter={{ active: true }} />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="documentTypeId"
            label="Tipo de Documento"
            rules={[requiredRule]}
          >
            <DocumentTypesInfiniteSelectField
              queryFilter={{ active: true }}
              disabled
            />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="payDate"
            label="Data de Pagamento"
            rules={[requiredRule]}
          >
            <InputDate
              disabled={
                !hasPermissions([
                  Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_EDIT_DATE_SETTLE_INSTALLMENT,
                ])
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="dueDate"
            label="Vencimento da nova parcela"
            rules={[requiredRule]}
          >
            <InputDate />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="amountToBeSettled"
            label="Valor a ser liquidado"
            rules={[
              {
                required: true,
                type: 'number',
                message: 'Digite um valor maior que R$ 0,00',
                min: 0.01,
              },
              {
                type: 'number',
                max: maxAmountToBeSettled - 0.01,
                message: 'Valor deve ser menor que o valor líquido',
              },
            ]}
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col span={12}>
          <Statistic
            title="Valor líquido"
            value={netValue}
            formatter={(value) => formatterPrice(Number(value))}
            valueStyle={{
              color:
                netValue > 0 ? '#148e28' : netValue < 0 ? '#ba4242' : undefined,
            }}
          />
        </Col>

        <Col span={12}>
          <Statistic
            title="Total a receber"
            value={maxAmountToBeSettled}
            formatter={(value) => formatterPrice(Number(value))}
            valueStyle={{
              color:
                maxAmountToBeSettled > 0
                  ? '#148e28'
                  : maxAmountToBeSettled < 0
                  ? '#ba4242'
                  : undefined,
            }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default SettleInstallmentPartiallyForm;
